// Select — 네이티브 select 래퍼. label/error/hint 패턴.
// 복잡한 커스텀 드롭다운은 DropdownMenu를 사용한다.

import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** options 배열로 제공하거나 children(<option>)으로 직접 제공 */
  options?: SelectOption[];
  placeholder?: string;
  children?: ReactNode;
}

/**
 * Locker UI — Select
 * @example
 * <Select label="유형" options={[{ value: 'note', label: '노트' }]} />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { label, error, hint, options, placeholder, className = '', id, children, ...rest },
    ref,
  ) => {
    const selectId = id ?? (label ? `select-${label.replace(/\s+/g, '-')}` : undefined);

    return (
      <div className="ui-input-wrap">
        {label && (
          <label htmlFor={selectId} className="ui-input-label">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={['ui-input', 'ui-select', error ? 'ui-input--error' : '', className]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={
            [error ? `${selectId}-error` : '', hint ? `${selectId}-hint` : '']
              .filter(Boolean)
              .join(' ') || undefined
          }
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options
            ? options.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>
                  {o.label}
                </option>
              ))
            : children}
        </select>
        {error && (
          <span id={`${selectId}-error`} className="ui-input-error" role="alert">
            {error}
          </span>
        )}
        {hint && !error && (
          <span id={`${selectId}-hint`} className="ui-input-hint">
            {hint}
          </span>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
